import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, FileText, Receipt, Coins,
  Award, MessageSquareWarning, LogOut, ChevronRight, Shield,
  ShieldCheck, FileBarChart, BookOpenCheck, Megaphone,
  Users, Settings as SettingsIcon, MapPin, Calculator,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";

// Digital G-Book aligned navigation
// Strictly Digital G-Book module sequence
const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard", mod: "dashboard" },
  { to: "/properties", icon: FileText, key: "assessment", mod: "properties" },
  { to: "/tax", icon: Receipt, key: "taxReceipt", mod: "tax" },
  { to: "/other-tax", icon: Coins, key: "otherTax", mod: "other-tax" },
  { to: "/tax-engine", icon: Calculator, key: "demand", mod: "tax-engine" },
  { to: "/certificates", icon: Award, key: "certificates", mod: "certificates" },
  { to: "/notices", icon: Megaphone, key: "notices", mod: "notices" },
];

const NAV_GOVERNANCE = [
  { to: "/governance", icon: BookOpenCheck, key: "resolutionBook", mod: "governance" },
  { to: "/cashbook", icon: BookOpenCheck, key: "rozmel", mod: "cashbook" },
  { to: "/reports", icon: FileBarChart, key: "reports", mod: "reports" },
  { to: "/village-areas", icon: MapPin, key: "villageAreas", mod: "village-areas" },
  { to: "/users", icon: Users, key: "userMgmt", mod: "users" },
  { to: "/settings", icon: SettingsIcon, key: "settings", mod: "settings" },
];

export const OfficerLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const loc = useLocation();
  const nav = useNavigate();

  // Module-rights: if user has rights array, restrict nav to those modules.
  // Otherwise (legacy users or admins without rights set) show all.
  const allowed = (mod) => {
    const rights = user?.module_rights;
    if (!rights || rights.length === 0) return true;
    // Admin roles always see everything
    if (["sarpanch", "secretary", "district", "state"].includes(user?.role)) return true;
    return rights.includes(mod);
  };

  const handleLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex officer-shell">
      {/* Sidebar */}
      <aside className="sidebar w-64 shrink-0 text-white flex flex-col" style={{ background: "#0A3161" }} data-testid="officer-sidebar">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#E87722] flex items-center justify-center font-heading font-bold">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">HSPS</div>
              <div className="font-heading font-semibold text-sm leading-tight">{t("govOfHaryana")}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Modules</div>
          {NAV.filter((n) => allowed(n.mod)).map((n) => {
            const Icon = n.icon;
            const active = loc.pathname === n.to || (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={`nav-item ${active ? "active" : ""}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{t(n.key)}</span>
              </Link>
            );
          })}
          <div className="px-3 mt-4 mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Administration</div>
          {NAV_GOVERNANCE.filter((n) => allowed(n.mod)).map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.key}`} className={`nav-item ${active ? "active" : ""}`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span>{t(n.key)}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2 px-1">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold">
              {user?.full_name?.charAt(0) || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/60">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-md bg-white/10 hover:bg-white/20 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="app-header h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/dashboard" className="hover:text-[#0A3161]">{t("dashboard")}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-900 font-medium capitalize">
              {loc.pathname.split("/")[1] || t("dashboard")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link to="/" className="text-xs text-slate-600 hover:text-[#0A3161]">{t("home")}</Link>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-x-auto">
          <div className="fade-up">{children}</div>
        </main>
      </div>
    </div>
  );
};
